import { AnimatedContentLoader, Shimmer } from '@components'
import { useBusinessContext } from '@context'
import { fetchEntries } from '@query'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { EntryRow } from './EntryRow'
import styled from 'styled-components'

interface Props {
  documentId: number
  /** Show shimmer placeholders instead of a spinner while loading. */
  useShimmerLoading?: boolean
}

export const EntryOverview: React.FC<Props> = ({ documentId, useShimmerLoading = false }) => {
  const { slug } = useBusinessContext()
  const [t] = useTranslation()
  const { data, isLoading } = useQuery({
    queryKey: [slug, 'documents', documentId, 'entries'],
    queryFn: () => fetchEntries({ slug, documentId }, { page_size: 1000 })
  })
  const entries = data?.results || []
  const isEmpty = entries.length === 0

  const sortedEntries = useMemo(
    () => entries.sort((el1, el2) => el1.account - el2.account),
    [entries]
  )

  return (
    <EntryRowListLoader>
      <AnimatedContentLoader
        isLoading={isLoading}
        isEmpty={isEmpty}
        isEmptyDescription={t('document.noEntries')}
        loadingContent={
          useShimmerLoading ? (
            <EntryShimmerList>
              <Shimmer height="1.25rem" width="100%" />
              <Shimmer height="1.25rem" width="92%" />
            </EntryShimmerList>
          ) : undefined
        }
      >
        <EntryRowList>
          {sortedEntries.map(entry => (
            <EntryRow key={entry.id} entry={entry} />
          ))}
        </EntryRowList>
      </AnimatedContentLoader>
    </EntryRowListLoader>
  )
}

const EntryRowList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  min-width: 0;
`

const EntryRowListLoader = styled.div`
  padding: ${({ theme }) => theme.spacing.sm}rem;
  border: 1px solid ${({ theme }) => theme.colors.neutralGray};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  min-width: 0;
`

const EntryShimmerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`
